import type { NavigationGuard, Router } from "vue-router"
import { getToken } from "../composables/embed/useEmbedAuth"
import { registerMessageCallbacks, unregisterMessageCallbacks } from "../composables/embed/useEmbedMessaging"
import { isEmbeddedContext, sendToParent } from "../services/embed-service"

export function createEnsureEmbedded(getRouter: () => Router): NavigationGuard {
  return (_to, _from) => {
    if (!isEmbeddedContext()) {
      getRouter().push({ name: "home" }).then()
      return false
    }
    return true
  }
}

export function createEnsureNotEmbedded(getRouter: () => Router, embedHomeName: string): NavigationGuard {
  return (to, _from) => {
    const isEmbeddable = to.matched.some((record) => record.meta.embeddable)
    if (isEmbeddable) {
      return true
    }

    if (isEmbeddedContext()) {
      getRouter().push({ name: embedHomeName }).then()
      return false
    }
    return true
  }
}

const EMBED_AUTH_TIMEOUT_MS = 15_000

export const ensureEmbedAuthenticated: NavigationGuard = async (_to, _from) => {
  const token = getToken()

  if (token) return true

  return new Promise<boolean>((resolve) => {
    let resolved = false

    const cleanup = () => {
      clearTimeout(timeout)
      unregisterMessageCallbacks("embed-auth-guard")
    }

    const timeout = setTimeout(() => {
      if (resolved) return
      resolved = true
      cleanup()
      console.error("[embed-auth] Timeout: access token not received within 15s")
      sendToParent("AUTH_ERROR", {
        code: "SESSION_INIT_TIMEOUT",
        message: "Access token not received within timeout"
      })
      resolve(false)
    }, EMBED_AUTH_TIMEOUT_MS)

    registerMessageCallbacks("embed-auth-guard", {
      onInit: () => {
        if (resolved) return
        if (!getToken()) return
        resolved = true
        cleanup()
        resolve(true)
      }
    })

    // Re-check after registering callback — closes race where
    // VITABLE_CONNECT_INIT arrived between initial check and callback registration
    if (getToken() && !resolved) {
      resolved = true
      cleanup()
      resolve(true)
    }
  })
}
